import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Bookmark, MapPin, PoundSterling, Briefcase, Trash2, ExternalLink, Loader2 } from 'lucide-react';
import { formatMoney } from '../lib/currency';
import PaginationBar from '../components/PaginationBar';

const PAGE_SIZE = 10;

export default function SavedJobsPage() {
  const { api } = useAuth();
  const [savedJobs, setSavedJobs] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchSaved(); }, []);

  // Removing the last job on a page moves back to the previous page.
  useEffect(() => {
    const last = Math.max(1, Math.ceil(savedJobs.length / PAGE_SIZE));
    if (page > last) setPage(last);
  }, [savedJobs.length, page]);

  const fetchSaved = async () => {
    try {
      const res = await api.get('/bookmarks');
      setSavedJobs(res.data);
    } catch { toast.error('Failed to load saved jobs'); }
    finally { setLoading(false); }
  };

  const unsave = async (jobId) => {
    try {
      await api.delete(`/bookmarks/${jobId}`);
      setSavedJobs(prev => prev.filter(s => s.job_id !== jobId));
      toast.success('Job removed from saved');
    } catch { toast.error('Failed to remove job'); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-[#003366]" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F0F4F8]">
      <div className="bg-[#003366] text-white py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-2">
            <Bookmark className="w-7 h-7" />
            <h1 className="text-3xl font-bold">Saved Jobs</h1>
          </div>
          <p className="text-white/70">{savedJobs.length} job{savedJobs.length !== 1 ? 's' : ''} saved</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {savedJobs.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <Bookmark className="w-14 h-14 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">No saved jobs yet</h3>
              <p className="text-muted-foreground mb-6">Browse jobs and click the bookmark icon to save them here.</p>
              <Link to="/jobs">
                <Button className="bg-[#003366] hover:bg-[#003366]/90">Browse Jobs</Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {savedJobs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map(saved => {
              const job = saved.job || saved;
              return (
                <Card key={saved.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-lg">{job.title || saved.job_title}</h3>
                          {job.remote && <Badge className="bg-emerald-100 text-emerald-700 text-xs">Remote</Badge>}
                        </div>
                        <p className="text-muted-foreground font-medium">{job.company_name || saved.company_name}</p>
                        <div className="flex flex-wrap gap-4 mt-3 text-sm text-muted-foreground">
                          {(job.location || saved.location) && (
                            <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{job.location || saved.location}</span>
                          )}
                          {(job.salary_min || saved.salary_min) && (
                            <span className="flex items-center gap-1">
                              <PoundSterling className="w-3.5 h-3.5" />
                              {formatMoney(job.salary_min || saved.salary_min, job.currency || saved.currency)} – {formatMoney(job.salary_max || saved.salary_max, job.currency || saved.currency)}
                            </span>
                          )}
                          {job.job_type && (
                            <span className="flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" />{job.job_type?.replace('_', ' ')}</span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">Saved {new Date(saved.saved_at).toLocaleDateString('en-GB')}</p>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <Link to={`/jobs/${saved.job_id}`}>
                          <Button size="sm" className="bg-[#003366] hover:bg-[#003366]/90">
                            <ExternalLink className="w-3.5 h-3.5 mr-1" />View
                          </Button>
                        </Link>
                        <Button size="sm" variant="ghost" onClick={() => unsave(saved.job_id)} className="text-red-500 hover:text-red-700 hover:bg-red-50">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            <PaginationBar page={page} pageSize={PAGE_SIZE} total={savedJobs.length} onPageChange={setPage} />
          </div>
        )}
      </div>
    </div>
  );
}
